import { type InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDebouncedCallback } from "@react-hookz/web";
import { atom, useAtomValue, useStore } from "jotai";

import { type ChartSettings, sampleConfig } from "../../../../../data/live-charts/config";
import type { QuestCandleData } from "../../../../../context/quest/api";
import { calculatePastTimeWithMarketGaps } from "../../utils/time";
import { useQuestApi } from "../../../../../context/quest";
import { useQueryCache } from "../../../../../utils/query";
import { useChartDataContext, useChartView } from "../../../context";
import { utc } from "../../../util";
import { objectKeys } from "../../../../../utils";
import { indicatorLookback } from "../../utils";
import { updateCloses } from "./helpers";
import { useOnWake } from "./useOnWake";
import { logger } from "@artis/logger";

export type Page = { data: QuestCandleData[]; ended: boolean };
export type PageParams = { from: number; to: number; page: number };

const CANDLE_PADDING = 4; // How many candles-worth of padding to have on the right when restoring formula-based chart visible range.
const RIGHT_EDGE_PADDING = 2; // Candles of breathing room between the latest candle and the right edge on first load, so the live candle isn't flush against the edge.

// For charts with an expression, we want to keep track of the loading state of all the historical queries. The key is the chart ID, the value is a map where the key is the productId + sampleTime + periodFrom and the value is a boolean indicating whether the query is fetching or not.
const fetchingMap = atom<Record<string, Record<string, boolean>>>({});

export const useHistorical = (
  chart?: Partial<
    Pick<
      ChartSettings,
      "id" | "productId" | "sampleTime" | "periodFrom" | "artisType" | "editable" | "expression" | "indicatorsState"
    >
  >,
  enabled?: boolean,
) => {
  const client = useQueryClient();
  const view = useChartView();
  const quest = useQuestApi();
  const store = useStore();

  const { setLoading, setRecovering } = useChartDataContext();

  const cacheKey = ["chartHistorical", chart?.productId, chart?.sampleTime, chart?.periodFrom];

  const cacheKeys = useMemo(
    () =>
      objectKeys(sampleConfig).map((sampleTime) => ({
        sampleTime,
        key: ["chartHistorical", chart?.productId, sampleTime, chart?.periodFrom],
      })),
    [chart?.productId, chart?.periodFrom],
  );

  const cache = useQueryCache<InfiniteData<Page, PageParams>>(cacheKey);
  const enable = !!chart?.productId && !!chart?.sampleTime && !!chart?.periodFrom && enabled !== false;

  const config = sampleConfig[chart?.sampleTime ?? "10s"];

  const largestPeriod = useMemo(() => indicatorLookback(chart?.indicatorsState), [chart?.indicatorsState]);
  const padding = 30 + largestPeriod;

  const initialPageParam = useMemo(() => {
    const candles = store.get(view.visible) + padding;
    const to = utc().valueOf();
    const from = calculatePastTimeWithMarketGaps(to, config.seconds, candles);
    return { page: 0, from, to };
  }, [config.seconds, store, view, padding]);

  const historical = useInfiniteQuery({
    enabled: enable,
    queryKey: cacheKey,
    initialPageParam,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    staleTime: 1000 * 60 * 60,
    queryFn: async (p) => {
      if (!chart?.productId) return Promise.reject(new Error("No product ID"));

      const result = await quest.historical(
        {
          id: chart.productId,
          period_from: chart.periodFrom || "",
          sample: chart.sampleTime || "10s",
          field: chart.editable ? "value" : "fv",
          isCalc: chart.artisType === "customer_curve",
          from: p.pageParam.from,
          to: p.pageParam.to,
        },
        p,
      );

      // When we refetch on window focus we dont want to drop the live candle data
      if (p.pageParam.page === 0) {
        const existing = cache.get()?.pages?.[0]?.data?.slice(0, 2) ?? [];
        const data = mergeCandles(existing, result.data);
        return { data, ended: false };
      }

      return result;
    },
    getNextPageParam: (lastPage, _allPages, prev) => {
      if (!lastPage || lastPage.ended) return undefined;
      const range = store.get(view.range);
      const gap = Math.round(Math.abs(range.from - padding)) + padding;
      const to = prev.from;
      const from = calculatePastTimeWithMarketGaps(to, config.seconds, gap);
      return { page: (prev.page ?? 0) + 1, to: prev.from, from: Math.min(prev.from, from) };
    },
  });

  const chartKey = `${chart?.productId}-${chart?.sampleTime}-${chart?.periodFrom}`;
  const loadingState = useAtomValue(fetchingMap);

  useEffect(() => {
    if (!chart?.id || !chart?.productId || !chart?.sampleTime || !chart?.periodFrom) return;
    store.set(fetchingMap, (x) => ({
      ...x,
      [chart.id ?? ""]: {
        ...(x[chart.id ?? ""] ?? {}),
        [chartKey]: historical.isFetching,
      },
    }));
  }, [historical.isFetching, chart?.id, chart?.productId, chart?.sampleTime, chart?.periodFrom, store, chartKey]);

  /** Restore the viewport to something less than the max */
  const fetching = Object.values(loadingState[chart?.id ?? ""] ?? {}).some((x) => x);
  const max = historical.data?.pages?.reduce((acc, page) => acc + page.data.length, 0);
  useEffect(() => {
    if (!chart?.id || !chart?.sampleTime || fetching || !max) return;
    view.restore(chart?.expression ? max + CANDLE_PADDING : max + RIGHT_EDGE_PADDING);
  }, [chart?.id, chart?.sampleTime, fetching, view, max, chart?.expression]);

  // This makes sure we dont have a gap in the historical data
  // We do this as a seperate query because fetching only a few of the latest
  // candles is much cheaper than revalidating the entire historical data
  const interval = config.seconds * 1000;
  const latest = historical.data?.pages?.[0]?.data[0]?.[0];
  const sync = useQuery({
    queryKey: ["sync", historical.isFetching, latest, cacheKey],
    enabled: enable && !historical.isFetching && !historical.isLoading,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    queryFn: async (p) => {
      const latest = cache.get()?.pages?.[0]?.data?.[0]?.[0];
      // If we dont have previous it means historical hasn't been fetched yet
      if (!chart?.productId || historical.isFetching || !latest) {
        return null;
      }

      try {
        const data = await quest.historical(
          {
            id: chart.productId,
            period_from: chart.periodFrom || "",
            sample: chart.sampleTime || "10s",
            field: chart.editable ? "value" : "fv",
            isCalc: chart.artisType === "customer_curve",
            from: latest - interval * 5,
            to: latest,
          },
          p,
        );

        mergeIn(data.data);
      } catch (error) {
        if (isAbortError(error, p.signal)) return null;
        logger.error("Failed to sync historical data", { error, chart });
      } finally {
        setLoading(false);
      }

      return null;
    },
  });

  /** Merge new candle data into the historical query cache */
  const mergeIn = useCallback(
    (data: QuestCandleData[], live?: boolean) => {
      cache.set((x) => {
        if (!x) return undefined;
        const first = mergeCandles(x.pages[0].data, data);
        const pages = [{ data: first, ended: false }, ...x.pages.slice(1)];
        const pageParams = [{ ...x.pageParams[0], to: first[0][0] }, ...x.pageParams.slice(1)];
        return { ...x, pages, pageParams };
      });

      // If the merge is a live candle one, we want to iterate over all other caches (based on sampleTime), and merge only the close price of the live candle, so that all sampleTimes have the most up to date close price.
      if (live) {
        updateCloses(client, cacheKeys, data, chart?.sampleTime ?? "10s");
      }
    },
    [cache, client, cacheKeys, chart?.sampleTime],
  );

  // Different from the `sync` above, in that: After waking from sleep the historical query's window is frozen at mount time (useInfiniteQuery refetches each page with its stored params, so a focus refetch never reaches "now"), and the live stream may have missed many candles while suspended. Fetch exactly the gap between the newest cached candle and now and merge it in, so the chart fills back to live rather than showing a hole.
  const recoverGap = useCallback(async () => {
    if (!enable || !chart?.productId) return;
    const latest = cache.get()?.pages?.[0]?.data?.[0]?.[0];
    if (!latest) return;
    const to = utc().valueOf();
    // The live stream / sync query already cover sub-candle gaps.
    if (to - latest < interval) return;
    // Cover the chart while we backfill: the overlay hides the page-rebuild churn and blocks interaction, and tells auto-scroll to snap back to the latest once we're done.
    setRecovering?.(true);
    try {
      logger.info("Recovering chart historical gap after wake", {
        tags: ["lifecycle"],
        chartKey,
        gapMs: to - latest,
      });
      const data = await quest.historical({
        id: chart.productId,
        period_from: chart.periodFrom || "",
        sample: chart.sampleTime || "10s",
        field: chart.editable ? "value" : "fv",
        isCalc: chart.artisType === "customer_curve",
        from: latest,
        to,
      });
      // Wait for historical's focus/reconnect refetch to settle first; its page-0 rebuild keeps only the 2 newest candles, so merging before it lands would prune our gap.
      const histKey = ["chartHistorical", chart.productId, chart.sampleTime, chart.periodFrom];
      for (let i = 0; i < 50 && client.getQueryState(histKey)?.fetchStatus === "fetching"; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      mergeIn(data.data);
    } catch (error) {
      if (isAbortError(error)) return;
      logger.error("Failed to recover chart historical gap after wake", { error, chart });
    } finally {
      setRecovering?.(false);
    }
  }, [enable, chart, cache, interval, quest, mergeIn, chartKey, client, setRecovering]);

  // Only run after a real gap (a brief tab switch is handled by the live stream), capped so a long sleep still triggers exactly one catch-up fetch.
  useOnWake(() => void recoverGap(), { minHiddenMs: Math.min(Math.max(interval, 30_000), 120_000) });

  // As soon as we get first live candle we refetch the last few candles
  // This is to make sure we dont have a gap in the historical data
  const initialRef = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: because it is needed to reset the initialRef
  useEffect(() => {
    initialRef.current = true;
  }, [chart]);

  const onLive = useCallback(
    (data: QuestCandleData[]) => {
      mergeIn(data, true);
      if (initialRef.current) {
        initialRef.current = false;
        sync.refetch();
        return;
      }
    },
    [mergeIn, sync.refetch, sync],
  );

  const shouldNext = useRef(false);
  const fetchNext = useDebouncedCallback(
    () => {
      const range = store.get(view.range);
      if (range.from > -1 || !historical.hasNextPage) return Promise.resolve();
      if (historical.isFetching) {
        shouldNext.current = true;
        return Promise.resolve();
      }
      return historical.fetchNextPage().then(() => {
        if (shouldNext.current) {
          shouldNext.current = false;
          return fetchNext();
        }
      });
    },
    [view, historical],
    200,
  );

  // Top up the off-screen warmup buffer so lookback indicators reach the start
  // of the visible range. A moving average (and the band/RSI indicators) can't
  // be drawn for the first `largestPeriod` loaded candles, so we keep enough
  // candles loaded to sit that whole undrawn region to the left of the visible
  // window: at least `visible width + largestPeriod` in total.
  //
  // This is deliberately counted from the loaded candles rather than the view's
  // logical range: `view.range` goes stale right after restore() (the range
  // event it triggers fires while `restored` is still false and is dropped), so
  // reading it here would mislead us — most visibly on 4h charts, where the
  // market-gap time math under-delivers candles and the undrawn region can
  // intrude into the visible range without any x-axis interaction.
  //
  // A top-up is needed at all because the query key excludes indicators, so
  // toggling one on (or widening its period) after load doesn't refetch on its
  // own. Each completed fetch updates `historical`, re-running the kick effect
  // below to re-check until the buffer is satisfied or history is exhausted.
  const lastTopUp = useRef(0);
  const fetchBuffer = useDebouncedCallback(
    () => {
      if (largestPeriod <= 0 || !historical.hasNextPage || historical.isFetching) return Promise.resolve();
      const loaded = historical.data?.pages?.reduce((acc, page) => acc + page.data.length, 0) ?? 0;
      if (loaded >= store.get(view.visible) + largestPeriod) return Promise.resolve();
      // Bail if the previous top-up fetch added no candles (history exhausted
      // without an explicit `ended`) so we don't fetch empty pages in a loop.
      if (loaded > 0 && loaded === lastTopUp.current) return Promise.resolve();
      lastTopUp.current = loaded;
      return historical.fetchNextPage();
    },
    [view, historical, largestPeriod, store],
    200,
  );

  // Kick a top-up whenever the buffer might be short: on mount, after each
  // fetch settles (historical identity changes), when the required buffer grows
  // (indicator toggled on / period widened), or when the window widens.
  useEffect(() => {
    fetchBuffer();
  }, [fetchBuffer]);

  useEffect(() => {
    return store.sub(view.range, () => {
      if (store.get(view.range).from < 0) fetchNext();
    });
  }, [store, view, fetchNext]);

  return { historical, onLive, fetchNext };
};

const isAbortError = (error: unknown, signal?: AbortSignal): boolean => {
  if (signal?.aborted) return true;
  return error instanceof DOMException && error.name === "AbortError";
};

/** Prepend more recent candles to current */
const mergeCandles = (current: QuestCandleData[], incoming: QuestCandleData[]): QuestCandleData[] => {
  const candleMap = new Map<number, QuestCandleData>();

  for (const candle of current) candleMap.set(candle[0], candle);
  for (const candle of incoming) candleMap.set(candle[0], candle);
  const merged = Array.from(candleMap.values());
  merged.sort((a, b) => b[0] - a[0]);
  return merged;
};

export const useCandlesCache = (
  chart?: Partial<Pick<ChartSettings, "id" | "productId" | "sampleTime" | "periodFrom" | "artisType" | "editable">>,
) =>
  useQueryCache<InfiniteData<Page, PageParams>>([
    "chartHistorical",
    chart?.productId,
    chart?.sampleTime,
    chart?.periodFrom,
  ]);
